/** core libraries & third party packages */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";

/** icons */
import toast from "react-hot-toast";
import { BsCart4 } from "react-icons/bs";
import { MdArrowOutward } from "react-icons/md";

/** component */

/** services */
import {
  capturePayment,
  getProductById,
  verifyPayment,
} from "../../services/api";
import { RAZORPAY_KEY } from "../../services/config";

/** custom hooks & query & context */
import { useCartQuery, useOrderQuery } from "../../hooks/useReactQuery";

/** utils */
import { handleGetUserFromLocalStorage } from "../../utils/common";

/** style */
import "./Checkout.scss";

/** interfaces */
import { IUser } from "../../Interfaces/user";
import { IProduct } from "../../Interfaces/product";
import { CartItem } from "../../Interfaces/cart";
import {
  IOrder,
  IPaymentStatus,
  IShippingAddress,
  IStatus,
} from "../../Interfaces/order";

interface ICartWithProducts extends CartItem {
  product: IProduct | undefined;
}
const Checkout = () => {
  const navigate = useNavigate();
  const [cartsWithProduct, setCartsWithProduct] =
    useState<ICartWithProducts[]>();
  const [loadingLocation, setLoadingLocation] = useState(false);

  const currentUser = JSON.parse(
    handleGetUserFromLocalStorage() as string
  ) as IUser;

  const { Prefetch: prefetchGetAllCart } = useCartQuery();
  const getAllCarts = prefetchGetAllCart(currentUser?._id);
  const [cartProductIds, setCartProductIds] = useState<string[]>([]);
  const { createMutation } = useOrderQuery();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<IShippingAddress>({ mode: "all" });

  // calculate cart summary;
  const orderSummary = useMemo(() => {
    const subtotal =
      cartsWithProduct?.reduce(
        (acc, item) => acc + item.price * item.quantity,
        0
      ) ?? 0;

    const discount = subtotal * 0;
    const deliveryFee = 100;
    const total = subtotal - discount + deliveryFee;

    return {
      subtotal: +subtotal.toFixed(2),
      discount: +discount.toFixed(2),
      deliveryFee: +deliveryFee.toFixed(2),
      total: +total.toFixed(2),
    };
  }, [cartsWithProduct]);

  useEffect(() => {
    (async () => {
      if (getAllCarts.data?.data !== undefined) {
        const productIds = getAllCarts.data.data.map(
          (cart: CartItem) => cart.productId
        );
        setCartProductIds(productIds);
        const productDetails = await handleFetchProductsByIds(productIds);
        // console.log("productDetails", productDetails);
        const cartWithProducts = getAllCarts.data.data.map((item: CartItem) => {
          const product = productDetails.find(
            (product: IProduct) => product._id === item.productId
          );
          return { ...item, product };
        });
        setCartsWithProduct(cartWithProducts);
        // console.log("cartWithProducts", cartWithProducts);
      }
    })();
  }, [getAllCarts.data?.data]);

  const handleFetchProductsByIds = async (productIds: string[]) => {
    const productRequests = productIds.map((id) => {
      return getProductById(id).then((res) => res.data);
    });
    const productDetails = await Promise.all(productRequests);
    return productDetails.map((product) => product);
  };


  const handleGetCurrentLocation = async () => {
    setLoadingLocation(true);
    try {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;

        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`
        );
        const data = await res.json();
        console.log("data", data);

        setValue("street", data.display_name);
        setValue("city", data.address.city);
        setValue("state", data.address.state);
        setValue("country", data.address.country);
        setValue("zipCode", data.address.postcode);
        setLoadingLocation(false);
      });
    } catch (err) {
      console.error("Error fetching location", err);
      setLoadingLocation(false);
    }
  };

  const handleLoadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";

      script.onload = () => resolve(true);
      script.onerror = () => resolve(true);
      document.body.appendChild(script);
    });
  };

  const handleBuyProduct = async (orderAddress: IShippingAddress) => {
    if (!orderAddress) {
      alert("Please add shipping Address to processed order");
      return;
    }
    const res = await handleLoadRazorpayScript();
    if (!res) {
      alert("Razorpay SDK failed to load");
      return;
    }

    const orderResponse = await capturePayment({
      productIds: cartProductIds,
      finalAmount: orderSummary.total,
    })
      .then((res) => {
        return res;
      })
      .catch((err) => {
        console.log("e", err);
        return err;
      });

    if (!orderResponse.success) {
      alert(orderResponse.message);
      return;
    }
    console.log("orderResponse", orderResponse);
    if (orderResponse.order !== undefined) {
      const options = {
        key: RAZORPAY_KEY,
        amount: orderResponse.order.amount,
        currency: "INR",
        name: "Kicks",
        description: "Product Purchase",
        // image: "", // optional
        order_id: orderResponse.order.id,
        handler: async function (response: {
          razorpay_order_id: any;
          razorpay_payment_id: any;
          razorpay_signature: any;
        }) {
          // console.log("response", response);
          const verifyRes = await verifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            productIds: cartProductIds,
            userId: currentUser._id,
          });
          if (verifyRes.success) {
            const cartProductsPayload = cartsWithProduct?.map((cartProduct) => {
              return {
                productId: cartProduct.productId,
                size: cartProduct.size,
                quantity: cartProduct.quantity,
                price: cartProduct.price,
                color: cartProduct.color,
              };
            });
            const orderPayload: IOrder = {
              userId: currentUser._id,
              products: cartProductsPayload as CartItem[],
              shippingAddress: orderAddress,
              totalAmount: orderResponse.order.amount,
              status: IStatus.Pending,
              paymentStatus: IPaymentStatus.Paid,
            };
            createMutation
              .mutateAsync(orderPayload)
              .then(async (res) => {
                toast.success("Purchase completed successfully!");
                navigate("/order-complete");
                getAllCarts.refetch();
              })
              .catch((error) => {
                console.log("error", error);
              });
          } else {
            alert("Payment verification failed");
          }
        },
        prefill: {
          name: "Testing name",
          email: "testname@mailinator.com",
        },
        theme: {
          color: "#F37254",
        },
      };

      // console.log("options", options);

      const paymentObject = new (window as any).Razorpay(options);
      paymentObject.open();
    }
  };

  return (
    <div className="checkout_page w-[96%]">
      <div className="checkout_page_container">
        <div className="flex pt-10 gap-5  justify-between md:flex-row-reverse  flex-col-reverse">
          {cartsWithProduct?.length !== 0 && cartsWithProduct !== undefined && (
            <form
              className="lg:w-[50%] w-[100%] border border-[var(--whitish-gray-color)]  bg-white  rounded-md px-4 py-4 h-fit"
              onSubmit={(event) => {
                handleSubmit(handleBuyProduct)(event);
              }}
            >
              <div className="font-medium text-[18px]">Shipping Address</div>
              <div>
                <div className="shipping_address_form">
                  <div className="input_group">
                    <label htmlFor="street">Street Address*</label>
                    <input
                      type="text"
                      id="street"
                      placeholder="Enter street address"
                      className="input_field"
                      {...register("street", {
                        required: {
                          value: true,
                          message: "Street address is required",
                        },
                      })}
                    />
                    <div className="field_error">{errors.street?.message}</div>
                  </div>
                  <div className="input_group">
                    <label htmlFor="country">Country*</label>
                    <input
                      type="text"
                      id="country"
                      placeholder="Enter country"
                      className="input_field"
                      {...register("country", {
                        required: {
                          value: true,
                          message: "Country  is required",
                        },
                      })}
                    />
                    <div className="field_error">{errors.country?.message}</div>
                  </div>
                  <div className="input_group">
                    <label htmlFor="city">City*</label>
                    <input
                      type="text"
                      id="city"
                      placeholder="Enter city"
                      className="input_field"
                      {...register("city", {
                        required: {
                          value: true,
                          message: "City  is required",
                        },
                      })}
                    />
                    <div className="field_error">{errors.city?.message}</div>
                  </div>
                  <div className="flex justify-between items-center gap-5">
                    <div className="input_group">
                      <label htmlFor="state">State*</label>
                      <input
                        type="text"
                        id="state"
                        placeholder="Enter state"
                        className="input_field"
                        {...register("state", {
                          required: {
                            value: true,
                            message: "state  is required",
                          },
                        })}
                      />
                      <div className="field_error">{errors.state?.message}</div>
                    </div>
                    <div className="input_group">
                      <label htmlFor="zipCode">ZipCode*</label>
                      <input
                        type="text"
                        id="zipCode"
                        placeholder="Enter zipCode"
                        className="input_field"
                        {...register("zipCode", {
                          required: {
                            value: true,
                            message: "zipCode  is required",
                          },
                        })}
                      />
                      <div className="field_error">
                        {errors.zipCode?.message}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="currentLocation"
                    onChange={(e) => {
                      if (e.target.checked) {
                        handleGetCurrentLocation();
                      } else {
                        reset();
                      }
                    }}
                  />
                  <label htmlFor="currentLocation" className="cursor-pointer">
                    {loadingLocation
                      ? "Fetching location..."
                      : "Use Current Location"}
                  </label>
                </div>
              </div>
              <button
                className="bg-[var(--black-color)] button w-full relative text-center group py-3 rounded-3xl mt-5 flex cursor-pointer  justify-center items-center gap-2"
                type="submit"
              >
                <div className=" outline-none border-none text-white text-[15px]">
                  Place Order{" "}
                </div>
                <div className="text-white bg-white w-[8px] absolute translate-y-[-50%] sm:right-[35%] right-[20%] sm:group-hover:right-[30%] group-hover:right-[22%]  top-1/2 h-[8px] rounded-full duration-300  group-hover:w-[35px] group-hover:h-[35px]">
                  <div className=" group-hover:flex hidden  justify-center  items-center w-full h-full">
                    <MdArrowOutward color="black" />
                  </div>
                </div>
              </button>
            </form>
          )}
          <div
            className={`flex flex-col gap-[10px] ${
              cartsWithProduct !== undefined && cartsWithProduct.length !== 0
                ? "lg:w-[50%] w-full"
                : "w-[100%]"
            }`}
          >
            <div className="w-[100%] border border-[var(--whitish-gray-color)]  bg-white  rounded-md px-4 py-2">
              <div className=" font-medium text-[24px]">Your Bag</div>
              {cartsWithProduct !== undefined ? (
                <>
                  {cartsWithProduct.length !== 0 ? (
                    cartsWithProduct?.map((carts) => {
                      return (
                        <div className="flex gap-2  border-b border-[var(--whitish-gray-color)] py-4">
                          <div className=" w-[75px] h-[75px]">
                            <img
                              src={carts.variantImages?.[0]}
                              alt="product"
                              loading="lazy"
                              className=" object-cover w-full h-full rounded"
                            />
                          </div>
                          <div className="flex flex-col w-full">
                            <div className="flex justify-between w-full items-center">
                              <div className="font-medium sm:text-[15px] text-[13px]">
                                {carts.product?.name}
                              </div>

                              {/* <div className="text-[var(--danger-text-color)] font-semibold text-[20px] cursor-pointer">
                                  <RiDeleteBinLine />
                                </div> */}
                            </div>
                            <div className="flex justify-between items-end">
                              <div>
                                <div className="sm:text-[14px] text-[12px] font-normal">
                                  Size: <span>{carts.size}</span>
                                </div>
                                <div className="sm:text-[14px] text-[12px] font-normal">
                                  Quantity: <span>{carts.quantity}</span>
                                </div>
                                <div className="sm:text-[14px] text-[12px] font-normal">
                                  Color: <span>{carts.color}</span>
                                </div>
                              </div>
                              <div className="flex justify-between">
                                <div className=" font-medium sm:text-[20px] text-[17px]">
                                  $ {carts.price}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="h-[200px] flex justify-center items-center flex-col gap-5">
                      <div>
                        <BsCart4 size={30} />
                      </div>
                      Your cart is empty. Start adding your favorite items !
                    </div>
                  )}
                </>
              ) : (
                <>Loading...</>
              )}
            </div>
            {cartsWithProduct?.length !== 0 &&
              cartsWithProduct !== undefined && (
                <div className="w-[100%] rounded-md p-4 h-fit bg-white">
                  <div className=" text-[20px] font-medium">Order Summary</div>
                  <div className="grid  grid-cols-2 justify-between w-full pt-2">
                    <div className="flex flex-col gap-3">
                      <div className="text-[14px] font-normal text-[var(--text-gray-color)]">
                        Subtotal
                      </div>
                      <div className="text-[14px] font-normal text-[var(--text-gray-color)]">
                        Discount (0%)
                      </div>
                      <div className="text-[14px] font-normal text-[var(--text-gray-color)]">
                        Delivery Fee
                      </div>
                      <div className="text-[16px] font-medium">Total</div>
                    </div>
                    <div className="flex justify-end flex-col items-end gap-3">
                      <div className="text-[14px] font-normal">
                        ₹{orderSummary.subtotal}
                      </div>
                      <div className="text-[14px] font-normal">
                        ₹{orderSummary.discount}
                      </div>
                      <div className="text-[14px] font-normal">
                        ₹{orderSummary.deliveryFee}
                      </div>
                      <div className="font-medium text-[16px]">
                        ₹{orderSummary.total}
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
